import { useCallback } from 'react';
import { genUUIDV4 } from '@frontend/string';
import { useUploadMediaMutation } from '../../../../../hooks/api-hooks/use-media-upload';
import { useFillingExperienceContext } from '../../../provider';
import { useDynamicFormStore } from '../store';
import { createPreviewMediaId } from './media-id';

interface SignatureUploadOptions {
  /** Drop the field's earlier attachments first, so only this file is submitted for the field. */
  replace?: boolean;
}

/**
 * Uploads a signature artifact (signature image or signed PDF) and records it as a successful attachment of
 * the field, returning its media id. Throws on failure; callers own the error UI and reporting.
 */
export const useSignatureUpload = (fieldKey: string) => {
  const { isPreviewMode } = useFillingExperienceContext();
  const { addAttachment, clearFieldAttachments } = useDynamicFormStore(['addAttachment', 'clearFieldAttachments']);
  const { mutateAsync: uploadMedia } = useUploadMediaMutation();

  return useCallback(
    async (file: File, { replace = false }: SignatureUploadOptions = {}) => {
      // Template previews can't persist media; keep the file local so the flow still works end to end.
      const mediaId = isPreviewMode ? createPreviewMediaId(genUUIDV4()) : await uploadMedia({ file });
      if (replace) clearFieldAttachments(fieldKey);
      addAttachment({
        id: genUUIDV4(),
        fieldKey,
        file,
        status: 'success',
        mediaId,
      });
      return mediaId;
    },
    [isPreviewMode, uploadMedia, addAttachment, clearFieldAttachments, fieldKey]
  );
};
